import type { VinylAmznTrackLoadOptions } from '@amzn/vinyl-amzn'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import {
    type Clearable,
    createDisposer,
    type Disposable,
    logDebug,
    type Maybe,
    type ValueProvider,
} from '@amazon/vinyl-util'
import { AuthStatus } from '../env/auth'
import { ctx } from '../env/playbackContext'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import type { ImageFields } from '../gql/imageFieldsFrag'
import type { PlayableEntity } from '../gql/playableEntityFrag'
import type { PlayParams } from '../gql/playParamsFrag'
import type { FeedProvider } from '../util/FeedProvider'
import type { CreateCloudQueueOptions } from './cloudQueueOptions'

const { target } = ctx('PlaylistController')

export type PlaylistEntry = {
    readonly id: string
    readonly entity: PlayableEntity
    readonly title: string
    readonly albumTitle: string
    readonly artistName: string
    readonly duration: number
    readonly image: ImageFields | null
    /**
     * Track load options used by the player. Either supplied
     * directly, or as a thunk that resolves them lazily — useful for
     * video entries where PRS resolution costs a network round trip
     * and we want the UI (artwork, title, transport position) to
     * update before the manifest URL lands.
     */
    readonly trackLoadOptions: ValueProvider<VinylAmznTrackLoadOptions>
    /**
     * True when the entry should render the video stage. Authoritative
     * — driven by the queue source rather than by sniffing media
     * dimensions, which is racy during load. Audio entries leave this
     * undefined / false.
     */
    readonly video?: boolean
    /**
     * CloudQueue's per-instance reference id for this entry, when the
     * feed is backed by CloudQueue. Needed to anchor `queue(view)` to
     * the right item — without it the gateway returns no edges. Local
     * (non-cloud-queue) feeds leave this undefined.
     */
    readonly entityReferenceId?: string | null
    /**
     * CloudQueue's per-edge `metricId` — a FireFly metric-context JSON blob
     * carrying selection-source provenance (which entity the customer picked
     * and its source). Parsed lazily at metric-emit time. Absent for non-cloud
     * feeds and entities the gateway omits it on.
     */
    readonly metricId?: string | null
    /**
     * Per-entity playback capabilities the cloud queue gateway
     * returned for this edge (skip / scrub / loop-one gating).
     * Absent for non-cloud feeds and for entities the gateway
     * doesn't report caps on — consumers treat absence as permissive.
     */
    readonly capabilities?: EntityPlaybackCapabilities | null
    /**
     * Set on an entry started before its cloud queue exists; the queue later confirms
     * it (see `ManagedPlaylistController.replaceProvider`) or replaces it.
     */
    readonly eagerStartId?: string
    /** Set on the queue's entry for the track already playing, which keeps playing as is. */
    readonly continuesPlayback?: boolean
}

/**
 * Per-entity playback capabilities, projected off the cloud queue's
 * `QueueEntityCapability` edge into a typed, gql-free shape the
 * transport can gate on (skip next / previous, scrub, loop-one).
 * `isAllowed` booleans plus the optional skip-limit metadata the
 * gateway attaches to the `next` action.
 */
export interface EntityPlaybackCapabilities {
    readonly nextAllowed: boolean
    readonly previousAllowed: boolean
    readonly scrubForwardAllowed: boolean
    readonly scrubBackwardAllowed: boolean
    readonly loopOneAllowed: boolean
    /** Remaining skips before the gateway blocks `next`, when capped. */
    readonly remainingSkips?: number
    /** Upsell reason surfaced when a skip is denied (e.g. 'UNLIMITED_SKIPS'). */
    readonly upsell?: string
}

/**
 * Permissive default — everything allowed. Used when a feed or entity
 * carries no capabilities (non-cloud queues, empty queues), so nothing
 * regresses: absence means "don't gate".
 */
export const ALLOW_ALL_ENTITY_CAPABILITIES: EntityPlaybackCapabilities = {
    nextAllowed: true,
    previousAllowed: true,
    scrubForwardAllowed: true,
    scrubBackwardAllowed: true,
    loopOneAllowed: true,
}

/**
 * Capability flags returned by the cloud queue gateway alongside the
 * queue. Mirrors hornet's `QueueCapability` shape — the gateway
 * rejects setQueueShuffleMode / setQueueLoopMode for queues whose
 * `shuffle.isAllowed` / `loopAll.isAllowed` is false, so the
 * transport must consult these to enable / disable buttons rather
 * than always offering them.
 */
export interface QueueCapabilities {
    readonly shuffleAllowed: boolean
    readonly loopAllAllowed: boolean
    readonly blockQueueMutations: boolean
}

/**
 * Pure-functional feed of PlaylistEntry. The controller asks for the
 * next/previous entry relative to whichever entry it's currently on;
 * the feed handles cursor bookkeeping internally.
 */
export interface PlaylistDataProvider extends FeedProvider<PlaylistEntry> {
    /**
     * The parameters used to create the CloudQueue, if the playlist is
     * using CloudQueue.
     */
    readonly cloudQueueOptions?: Maybe<CreateCloudQueueOptions>

    /**
     * Capabilities the cloud queue gateway returned for this queue.
     * Absent for non-cloud feeds; transport defaults to "all
     * allowed" in that case.
     */
    readonly queueCapabilities?: Maybe<QueueCapabilities>
}

export interface PlaylistController extends Clearable {
    /** The currently-selected entry, or null if nothing is queued. */
    readonly current: ObservableValue<PlaylistEntry | null>

    /**
     * True only during the synchronous `current` emission of a
     * page-reload restore (see `ManagedPlaylistController.restoreProvider`).
     * Playback-side bindings that would otherwise start audio or grab a
     * stream slot on a `current` change consult this so a reload restores
     * the now-playing entry PAUSED instead of auto-playing it.
     */
    readonly restoringCurrent: boolean

    /**
     * Per-entity playback capabilities of the `current` entry, derived
     * from `current.capabilities`. Falls back to the permissive
     * `ALLOW_ALL_ENTITY_CAPABILITIES` when nothing is queued or the
     * entry carries no caps, so gating surfaces (skip / prev / scrub /
     * loop-one) default to enabled and nothing regresses.
     */
    readonly currentCapabilities$: ObservableValue<EntityPlaybackCapabilities>

    /**
     * The entry that will play after `current`. Refreshed automatically
     * whenever `current` changes (the controller asks the feed for
     * `next(current)` and writes the result here once it resolves).
     * Holds null while the lookup is in flight, when the feed has no
     * further items, when the lookup failed (see `nextUnknown$`), or when
     * no feed is set. Use `invalidateNext` to force a re-fetch — useful
     * when the feed's contents shift out from under us (e.g. a guest adds
     * a track to a party queue).
     */
    readonly next: ObservableValue<PlaylistEntry | null>

    /**
     * True when the last `next` lookup failed, so whether an entry follows
     * `current` is unknown — unlike a null `next` from a feed that has ended.
     * Transports keep next enabled in this state; `skipNext` looks it up again.
     */
    readonly nextUnknown$: ObservableValue<boolean>

    /** The CloudQueue options from the active feed, if any. */
    readonly cloudQueueOptions: ObservableValue<Maybe<CreateCloudQueueOptions>>

    /**
     * Capabilities reported by the active cloud queue. Null when no
     * feed is set or the feed didn't surface capabilities. Transport
     * surfaces consult `shuffleAllowed` / `loopAllAllowed` so the
     * shuffle / repeat buttons disable themselves on queues where
     * those mutations would 400 — gateway returns
     * "Shuffle not supported on this queue" otherwise.
     */
    readonly queueCapabilities: ObservableValue<Maybe<QueueCapabilities>>

    /** PlayParams projection of cloudQueueOptions, for convenience. */
    readonly playParams: ObservableValue<Maybe<PlayParams>>

    /**
     * Advance to the next entry. Updates `current` and returns the new
     * value. Resolves to null when the feed has nothing further; the
     * controller leaves current pointing at the last entry in that
     * case so transports can show the final track.
     */
    skipNext(): Promise<PlaylistEntry | null>

    /**
     * Step back to the previous entry. Same return semantics as
     * skipNext.
     */
    skipPrevious(): Promise<PlaylistEntry | null>

    /**
     * Refresh the `next` observable. Drops whatever lookup is cached
     * and asks the feed again from the current entry. Useful when
     * the feed's contents may have shifted (e.g. a guest added a
     * track to a party queue and the previously-resolved next is no
     * longer authoritative — or there was no next at all and now
     * there is one).
     */
    invalidateNext(): void

    /**
     * Monotonic counter that increments each time the underlying
     * queue's edges change due to a local mutation (addToQueue,
     * insertIntoQueue, removeFromQueue called via the controller).
     * Surfaces driven by the controller — the now-playing queue rail,
     * for instance — subscribe to this to know when to re-fetch their
     * own view of the queue. Distinct from `invalidateNext`, which
     * only nudges the next-prefetch.
     */
    readonly queueMutatedAt: ObservableValue<number>

    /**
     * Bump `queueMutatedAt`. Call after a successful queue-mutation
     * RPC (addToQueue, insertIntoQueue, removeFromQueue) so views
     * subscribed to the controller can refresh.
     */
    notifyQueueMutated(): void

    /**
     * Seek directly onto a specific entry inside the existing feed.
     * Used by surfaces that let the user pick a track from the queue
     * (e.g. the now-playing queue rail) — we want to start playing
     * the chosen track without re-creating the cloud queue, which
     * would lose the parent container context (album, playlist, …).
     *
     * The caller is responsible for ensuring the entry is one the
     * feed knows about (via its provider-specific cursor) — for the
     * cloud-queue feed that means seeding entityReferenceId via
     * registerEntryRef before calling.
     */
    seekToEntry(entry: PlaylistEntry): void

    /**
     * Local-only repeat mode. The cloud queue can carry its own
     * `loopMode` server-side (set at queue creation), but
     * mid-playback toggling routes through the controller — the
     * binding consults this when the player fires `ended` to decide
     * whether to seek-and-replay vs auto-advance.
     *
     * Cycles off → all → one with a single button:
     *   - off: fall through to skipNext on `ended`.
     *   - all: same as off; the cloud queue's own loopMode/feed
     *     terminator decides.
     *   - one: seek to 0 + play instead of advancing.
     */
    readonly repeatMode: MutableValue<RepeatMode>

    /**
     * Local shuffle toggle. When on, `skipNext()` from the transport
     * is rerouted through `skipNextRandom()` so playback jumps to a
     * random upcoming entry instead of the linear next.
     */
    readonly shuffleEnabled: MutableValue<boolean>

    /**
     * A seek offset (in seconds) queued for the next entry that
     * begins playing. Used by timestamped deep-links (`?t=90`) — the
     * route mounts, kicks off queue creation, and posts a pending
     * seek; the player-playlist binding applies it either
     * immediately (if the player is already playing) or on the next
     * `playing` event, then clears the value.
     */
    readonly pendingSeekSeconds: MutableValue<number | null>

    /**
     * Queue a seek to `seconds` for the current / next-to-play
     * entry. The player-playlist binding drains this the moment the
     * player is ready (playing or metadata-loaded), so callers can
     * fire-and-forget from route mount without racing queue
     * creation.
     */
    pendingSeek(seconds: number): void
}

export type RepeatMode = 'off' | 'all' | 'one'

/** A fixed list of entries, walked in order from `startIndex` without a cloud queue. */
export interface StaticFeedSpec {
    readonly entries: readonly PlaylistEntry[]
    readonly startIndex?: number
    /** Marks the feed as this container's, so its play control reads it as live. */
    readonly cloudQueueOptions?: Maybe<CreateCloudQueueOptions>
}

function staticFeed(spec: StaticFeedSpec): PlaylistDataProvider {
    const { entries, startIndex = 0 } = spec
    const at = (index: number) => Promise.resolve(entries[index] ?? null)
    const indexOf = (entry: PlaylistEntry) => entries.indexOf(entry)
    return {
        cloudQueueOptions: spec.cloudQueueOptions ?? null,
        next: (current) => {
            if (current == null) return at(startIndex)
            const index = indexOf(current)
            return at(index < 0 ? -1 : index + 1)
        },
        previous: (current) => at(indexOf(current) - 1),
    }
}

/**
 * A PlaylistController whose backing feed can be (re)set.
 */
export interface ManagedPlaylistController extends PlaylistController {
    setDataProvider(value: PlaylistDataProvider): void

    /** Install a {@link StaticFeedSpec} and advance onto its start entry, resolving to it. */
    playFeed(spec: StaticFeedSpec): Promise<PlaylistEntry | null>

    /**
     * Install a feed and park PAUSED on its seed (current) entry —
     * page-reload queue recovery. Unlike `setDataProvider` (which the play
     * path pairs with `skipNext()` and auto-plays via the `current`
     * subscription), this sets `current` under the `restoringCurrent` flag
     * so the playback-side bindings load the entry without starting audio.
     */
    restoreProvider(value: PlaylistDataProvider): Promise<void>

    /**
     * Install a feed while staying on the same media: `current` becomes `entry` with no
     * `null` emission between, so the playing engine keeps playing. The cloud queue's
     * confirmation of an eagerly started entry.
     */
    replaceProvider(value: PlaylistDataProvider, entry: PlaylistEntry): void
}

export class PlaylistControllerImpl implements ManagedPlaylistController, Disposable {
    get [Symbol.toStringTag]() {
        return 'PlaylistController'
    }

    private readonly _cloudQueueOptions = data<Maybe<CreateCloudQueueOptions>>(null)
    public readonly playParams: ObservableValue<Maybe<PlayParams>> =
        this._cloudQueueOptions.pick('playParams')

    public get cloudQueueOptions(): ObservableValue<Maybe<CreateCloudQueueOptions>> {
        return this._cloudQueueOptions
    }

    private readonly _queueCapabilities = data<Maybe<QueueCapabilities>>(null)
    public get queueCapabilities(): ObservableValue<Maybe<QueueCapabilities>> {
        return this._queueCapabilities
    }

    private readonly _current = data<PlaylistEntry | null>(null)
    public get current(): ObservableValue<PlaylistEntry | null> {
        return this._current
    }

    private _restoringCurrent = false
    public get restoringCurrent(): boolean {
        return this._restoringCurrent
    }

    public readonly currentCapabilities$: ObservableValue<EntityPlaybackCapabilities> =
        this._current.map((entry) => entry?.capabilities ?? ALLOW_ALL_ENTITY_CAPABILITIES)

    private readonly _next = data<PlaylistEntry | null>(null)
    public get next(): ObservableValue<PlaylistEntry | null> {
        return this._next
    }

    private readonly _nextUnknown$ = data(false)
    public get nextUnknown$(): ObservableValue<boolean> {
        return this._nextUnknown$
    }

    private readonly _queueMutatedAt = data(0)
    public get queueMutatedAt(): ObservableValue<number> {
        return this._queueMutatedAt
    }

    /**
     * Local play-mode flags. Both default off; the player binding
     * consults `repeatMode` on `ended`, and the transport's Next
     * button consults `shuffleEnabled` to decide between linear and
     * random advance. Persistence isn't wired — these reset to
     * defaults on a hard reload.
     */
    public readonly repeatMode: MutableValue<RepeatMode> = data<RepeatMode>('off')
    public readonly shuffleEnabled: MutableValue<boolean> = data(false)

    /**
     * Queued seek offset (seconds) draining in the player-playlist
     * binding. Bumped by `pendingSeek`; the binding clears it once
     * the seek has been dispatched to the player. See the
     * `pendingSeek` doc on the interface.
     */
    public readonly pendingSeekSeconds: MutableValue<number | null> = data<number | null>(null)

    pendingSeek(seconds: number): void {
        if (!Number.isFinite(seconds) || seconds < 0) return
        this.pendingSeekSeconds.value = seconds
    }

    notifyQueueMutated(): void {
        this._queueMutatedAt.value = this._queueMutatedAt.value + 1
        this.refreshNext()
    }

    private feed: PlaylistDataProvider | null = null

    /**
     * Set to true when a `skipNext()` call resolves with no next
     * entry — i.e. we've fallen off the tail of the feed. Cleared
     * any time we successfully advance/rewind/jump. While true, a
     * subsequent `refreshNext()` that resolves to a non-null entry
     * (e.g. a guest just added a track to the party queue) auto-
     * advances onto it instead of leaving the user marooned at the
     * end. See `refreshNext` for the auto-advance branch.
     */
    private atTail = false

    /**
     * Token for the in-flight `next` lookup. Bumped on every refresh
     * so older fetches can detect that they've been superseded and
     * not stomp `_next` with a stale value.
     */
    private nextToken = 0

    /**
     * Promise of the in-flight `next` lookup. `skipNext()` awaits this
     * to avoid issuing a duplicate feed.next() when the binding has
     * already kicked one off — the common case is "current changes →
     * preload-prefetch fires → user immediately hits the skip
     * button".
     */
    private nextPromise: Promise<PlaylistEntry | null> = Promise.resolve(null)

    /**
     * The in-flight step back and the entry it left from. Repeated presses while
     * it is out share it, as repeated skips share `nextPromise`, instead of each
     * fetching the same previous entry and reloading it.
     */
    private previousWalk: {
        readonly from: PlaylistEntry
        readonly result: Promise<PlaylistEntry | null>
    } | null = null

    private readonly disposer = createDisposer()

    constructor() {
        const { add } = this.disposer
        add(
            getPlaybackEnv().auth.status$.onData((status) => {
                if (status === AuthStatus.SIGNED_OUT) this.clear()
            })
        )
        // Whenever the current entry changes, kick off a fresh next
        // lookup so the `next` observable converges automatically.
        // First emission fires with current=null (no feed) → null
        // is the correct value.
        add(
            this._current.onData((entry) => {
                logDebug(target, 'current changed:', {
                    id: entry?.id ?? null,
                    title: entry?.title ?? null,
                    video: entry?.video === true,
                })
            })
        )
        // Toggling shuffle / repeat on a LOCAL feed changes which entry
        // comes after the current one, so drop the stale cached `next`.
        // Skip the first emission (initial defaults) so we don't fire a
        // pointless invalidate before any feed is set.
        const invalidateOnChange = (mode$: ObservableValue<unknown>) => {
            let first = true
            return mode$.onData(() => {
                if (first) {
                    first = false
                    return
                }
                // Cloud-queue toggles re-seed `next` via the refetch the
                // toggle handler runs; invalidating here would fire a
                // redundant NextTrackInQueue (GetNextEntity) against the
                // stale pre-mutation order and help trip FF's rate limit.
                if (this._cloudQueueOptions.value?.queueId) return
                this.invalidateNext()
            })
        }
        add(invalidateOnChange(this.shuffleEnabled))
        add(invalidateOnChange(this.repeatMode))
    }

    setDataProvider(provider: PlaylistDataProvider): void {
        this.clear()
        this.feed = provider
        this._cloudQueueOptions.value = provider.cloudQueueOptions ?? null
        this._queueCapabilities.value = provider.queueCapabilities ?? null
        this.nextPromise = this.feed.next(null)
    }

    playFeed(spec: StaticFeedSpec): Promise<PlaylistEntry | null> {
        this.setDataProvider(staticFeed(spec))
        return this.skipNext()
    }

    async restoreProvider(provider: PlaylistDataProvider): Promise<void> {
        this.clear()
        this.feed = provider
        this._cloudQueueOptions.value = provider.cloudQueueOptions ?? null
        this._queueCapabilities.value = provider.queueCapabilities ?? null
        const seed = await provider.next(null)
        // A newer feed superseded us while we awaited the seed (e.g. the user
        // started playing something) — don't clobber it with the restore.
        if (this.feed !== provider) return
        this.nextPromise = Promise.resolve(null)
        if (!seed) return
        this.atTail = false
        this._restoringCurrent = true
        try {
            this._current.value = seed
        } finally {
            this._restoringCurrent = false
        }
        this.refreshNext()
    }

    replaceProvider(provider: PlaylistDataProvider, entry: PlaylistEntry): void {
        this.feed = provider
        this._cloudQueueOptions.value = provider.cloudQueueOptions ?? null
        this._queueCapabilities.value = provider.queueCapabilities ?? null
        this._current.value = entry
        this.refreshNext()
    }

    clear(): void {
        this.feed = null
        this.nextToken++
        this._next.value = null
        this._nextUnknown$.value = false
        this._current.value = null
        this._cloudQueueOptions.value = null
        this._queueCapabilities.value = null
        this.atTail = false
        this.pendingSeekSeconds.value = null
    }

    async skipNext(): Promise<PlaylistEntry | null> {
        if (!this.feed) return null
        // Use the prefetched next promise.
        let next = await this.nextPromise.catch(() => null)
        if (next === null && this._current.value) {
            // If the prefetch found nothing or failed, try again with the feed provider.
            next = await this.feed.next(this._current.value)
        }
        if (next != null) {
            this.atTail = false
            this._current.value = next
            this.refreshNext()
        } else {
            // No next available — remember we've fallen off the
            // tail so a later invalidateNext() (e.g. a guest adds a
            // track to the party queue) can auto-advance onto the
            // new entry instead of leaving us stuck on the last
            // track. See refreshNext.
            this.atTail = true
        }
        return next
    }

    skipPrevious(): Promise<PlaylistEntry | null> {
        const from = this._current.value
        if (!this.feed || !from) return Promise.resolve(null)
        if (this.previousWalk?.from === from) return this.previousWalk.result
        const result = this.stepBack(this.feed, from)
        const walk = { from, result }
        this.previousWalk = walk
        const done = () => {
            if (this.previousWalk === walk) this.previousWalk = null
        }
        result.then(done, done)
        return result
    }

    private async stepBack(
        feed: PlaylistDataProvider,
        from: PlaylistEntry
    ): Promise<PlaylistEntry | null> {
        const prev = await feed.previous(from)
        if (prev != null) {
            this.atTail = false
            this._current.value = prev
            this.refreshNext()
        }
        return prev
    }

    invalidateNext(): void {
        this.refreshNext()
    }

    seekToEntry(entry: PlaylistEntry): void {
        if (!this.feed) return
        // Hop directly onto the entry. The entry feeder is
        // wired to current.onData so it'll load + play; we kick the
        // next lookup so transports show the correct disabled state.
        this.atTail = false
        this._current.value = entry
        this.refreshNext()
    }

    private refreshNext(): void {
        const my = ++this.nextToken
        const fromEntry = this._current.value
        if (!fromEntry || !this.feed) {
            this.nextPromise = Promise.resolve(null)
            this._next.value = null
            this._nextUnknown$.value = false
            return
        }
        const promise = this.feed.next(fromEntry)
        this.nextPromise = promise
        promise
            .then((next) => {
                // Only the most recent lookup wins — earlier ones can
                // race in if the user is rapidly skipping or the feed
                // is invalidated mid-flight.
                if (my !== this.nextToken) return
                this._next.value = next
                this._nextUnknown$.value = false
                // We previously fell off the tail (skipNext returned
                // null after the last track ended) and now the feed
                // has a fresh entry — auto-advance onto it. The
                // common trigger is a guest adding a track to the
                // party queue while playback is parked at the end.
                if (next != null && this.atTail) {
                    this.atTail = false
                    this._current.value = next
                    this.refreshNext()
                }
            })
            .catch(() => {
                if (my !== this.nextToken) return
                this._next.value = null
                this._nextUnknown$.value = true
            })
    }

    dispose() {
        this.clear()
        this.disposer.dispose()
    }
}

/**
 * Returns true if what the given playlistController is currently playing either matches the given playParams id or
 * one of its containers.
 *
 * @param playlistController
 * @param playParams
 */
export function isCurrentPlaylistOrContainer(
    playlistController: PlaylistController,
    playParams: Maybe<PlayParams>
): boolean {
    const id = playParams?.id
    if (!id) return false
    const currentlyPlayingParams = playlistController.playParams.value
    // Check if the playlist controller is set to a matching container.
    if (currentlyPlayingParams?.id === id) {
        return true
    }
    if (!currentlyPlayingParams?.containerIds?.length) return false
    return currentlyPlayingParams.containerIds.includes(id)
}
